/**
 * Central metadata for PlanBoardConnection's relationshipType (see types.ts) — label, arrow
 * color, and icon per type, plus the fixed display order. This is the single source of truth
 * PlanConnectionRelationshipPopover renders from and PlanPage.tsx resolves connector colors
 * from, kept here (rather than duplicated in either) so a future "Sync to GitHub" operation can
 * import the same definitions instead of re-deriving relationship semantics.
 *
 * Icons are hand-written inline SVGs (stroke='currentColor', no fill) — this codebase has no
 * icon library anywhere; matches PlanPage.tsx's own TIMELINE_MENU_*_ICON constants and
 * ProjectMenu.tsx's inline-icon buttons.
 */
import React from 'react';
import { PlanConnectionRelationshipType } from '../types';

export interface PlanConnectionRelationshipMeta {
    label: string;
    /** Arrow/button accent color — distinct from the per-node-type COLORS borders
     * (canvasUtils.tsx), so a typed connection never reads as coincidentally matching one. */
    color: string;
    icon: React.ReactNode;
}

/** Fixed left-to-right order the popover's buttons render in. */
export const PLAN_CONNECTION_RELATIONSHIP_TYPES: PlanConnectionRelationshipType[] = [
    'blocks',
    'enables',
    'relates-to',
    'split-into',
];

/** Arrow color for a connection with no relationshipType yet — the same orange
 * ConnectionOverlay.tsx and PlanPage's connectorColorForItem fallback already use elsewhere. */
export const DEFAULT_PLAN_CONNECTION_COLOR = '#f55d0b';

export const PLAN_CONNECTION_RELATIONSHIP_META: Record<PlanConnectionRelationshipType, PlanConnectionRelationshipMeta> = {
    // Closed padlock — paired with 'enables''s open padlock below so the two read together as
    // an obvious locked/unlocked opposite pair, rather than needing to be recognized individually.
    'blocks': {
        label: 'Blocks',
        color: '#ef4444',
        icon: (
            <svg width='14' height='14' viewBox='0 0 16 16' fill='none' xmlns='http://www.w3.org/2000/svg'>
                <path d='M5.5 7.2V5.4C5.5 3.8 6.6 2.6 8 2.6C9.4 2.6 10.5 3.8 10.5 5.4V7.2' stroke='currentColor' strokeWidth='1.4' strokeLinecap='round' />
                <rect x='4' y='7.2' width='8' height='6.2' rx='1.2' stroke='currentColor' strokeWidth='1.4' />
                <circle cx='8' cy='10' r='0.9' fill='currentColor' />
            </svg>
        ),
    },
    // Open padlock — same lock body as 'blocks', but the shackle is swung open (only anchored
    // on one side), the standard "unlocked" reading.
    'enables': {
        label: 'Enables',
        color: '#22c55e',
        icon: (
            <svg width='14' height='14' viewBox='0 0 16 16' fill='none' xmlns='http://www.w3.org/2000/svg'>
                <path d='M5.5 7.2V5.4C5.5 3.8 6.6 2.6 8 2.6C9.2 2.6 10.2 3.5 10.5 4.7' stroke='currentColor' strokeWidth='1.4' strokeLinecap='round' />
                <rect x='4' y='7.2' width='8' height='6.2' rx='1.2' stroke='currentColor' strokeWidth='1.4' />
                <circle cx='8' cy='10' r='0.9' fill='currentColor' />
            </svg>
        ),
    },
    // Chain link — the standard "linked/related" glyph (two overlapping capsule shapes),
    // recognizable independent of the other three icons' shared lock/fork vocabulary.
    'relates-to': {
        label: 'Relates to',
        color: '#60a5fa',
        icon: (
            <svg width='14' height='14' viewBox='0 0 16 16' fill='none' xmlns='http://www.w3.org/2000/svg'>
                <rect x='1.8' y='6.6' width='7' height='3.4' rx='1.7' transform='rotate(-45 5.3 8.3)' stroke='currentColor' strokeWidth='1.4' />
                <rect x='7.2' y='6.6' width='7' height='3.4' rx='1.7' transform='rotate(-45 10.7 8.3)' stroke='currentColor' strokeWidth='1.4' />
            </svg>
        ),
    },
    // One node forking into two — a small flowchart/git-branch-style split, reading as "this
    // becomes these two" regardless of arrow direction.
    'split-into': {
        label: 'Split into',
        color: '#a855f7',
        icon: (
            <svg width='14' height='14' viewBox='0 0 16 16' fill='none' xmlns='http://www.w3.org/2000/svg'>
                <path d='M8 12.4V8.6' stroke='currentColor' strokeWidth='1.4' strokeLinecap='round' />
                <path d='M8 8.6C8 7.4 5.8 6.8 5.4 5.3M8 8.6C8 7.4 10.2 6.8 10.6 5.3' stroke='currentColor' strokeWidth='1.4' strokeLinecap='round' fill='none' />
                <circle cx='8' cy='13.2' r='1.1' fill='currentColor' />
                <circle cx='5' cy='4.4' r='1.1' fill='currentColor' />
                <circle cx='11' cy='4.4' r='1.1' fill='currentColor' />
            </svg>
        ),
    },
};
